// engagement-components/shared.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Shared UI primitives matching FeedbackResponses / FeedbackSetting patterns
//
//  • selectStyles       — react-select styles matching SurveySelect.jsx
//  • RSelect            — drop-in <Form.Select> replacement using react-select
//  • DateRangePill      — pill DatePicker matching FeedbackResponses exactly
//  • RDatePicker        — single-date picker (react-datepicker)
//  • RightModal         — full-height right-panel modal (modal-right pattern)
//  • DownloadMenu       — CSV / XLS / Print dropdown matching FeedbackResponses
//  • AiAssistBtn        — inline AI button, controlled by AI_ENABLED flag
//  • AI_ENABLED         — static feature flag (flip to true to show AI buttons)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect } from "react";
import Select             from "react-select";
import DatePicker         from "react-bootstrap-daterangepicker";
import ReactDatePicker    from "react-datepicker";
import moment             from "moment";
import { Modal, Button, Dropdown, Spinner } from "react-bootstrap";
import { FaRobot, FaMagic, FaChevronDown } from "react-icons/fa";
import axios              from "axios";
import { apiUrl, authHeaders } from "./config";

import "bootstrap-daterangepicker/daterangepicker.css";
import "react-datepicker/dist/react-datepicker.css";

// ─── Static AI feature flag ───────────────────────────────────────────────────
// Set to true to show AI assist buttons throughout the platform.
// Keep false in production until OpenAI key is configured server-side.
export const AI_ENABLED = false;

// ─── React-Select shared styles (matches SurveySelect.jsx exactly) ───────────
export const selectStyles = {
  control: (base, state) => ({
    ...base,
    border:          "1px solid #0d6efd",
    borderRadius:    "6px",
    padding:         "2px 4px",
    minHeight:       "38px",
    boxShadow:       state.isFocused
      ? "0 0 4px rgba(13,110,253,0.5)"
      : "0 2px 2px rgba(13,110,253,0.12)",
    backgroundColor: state.isFocused ? "#e7f1ff" : "#f8f9fa",
    fontSize:        "0.9rem",
    cursor:          "pointer",
    transition:      "all 0.2s ease",
  }),
  option: (base, state) => ({
    ...base,
    fontSize:        "0.88rem",
    fontWeight:      state.isSelected ? 600 : 400,
    backgroundColor: state.isSelected ? "#0d6efd" : state.isFocused ? "#e7f1ff" : "#fff",
    color:           state.isSelected ? "#fff" : "#333",
    padding:         "6px 10px",
    cursor:          "pointer",
  }),
  placeholder:       (base) => ({ ...base, color: "#6c757d", fontSize: "0.88rem" }),
  singleValue:       (base) => ({ ...base, color: "#0d6efd", fontWeight: 600 }),
  multiValue:        (base) => ({ ...base, background: "#e0e7ff" }),
  multiValueLabel:   (base) => ({ ...base, color: "#3730a3", fontSize: "0.8rem" }),
  multiValueRemove:  (base) => ({ ...base, color: "#6366f1", ":hover": { background: "#c7d2fe", color: "#1e1b4b" } }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  clearIndicator:    (base) => ({ ...base, padding: "4px" }),
  valueContainer:    (base) => ({ ...base, padding: "0 6px" }),
  menuPortal:        (base) => ({ ...base, zIndex: 9999 }),
};

// ─── Compact variant (for use inside table headers / toolbars) ────────────────
export const selectStylesCompact = {
  ...selectStyles,
  control: (base, state) => ({
    ...selectStyles.control(base, state),
    minHeight:    "32px",
    padding:      "0px 2px",
    fontSize:     "0.82rem",
  }),
};

// ─── RSelect — drop-in replacement for <Form.Select> ─────────────────────────
// Props: options (array of {value,label} or plain strings), value, onChange,
//        placeholder, isMulti, isDisabled, isClearable, size, ...rest
export const RSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  isMulti     = false,
  isDisabled  = false,
  isClearable = true,
  size,            // "sm" uses compact styles
  ...rest
}) => {
  const opts = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );

  // Resolve controlled value
  const resolvedValue = isMulti
    ? opts.filter((o) => (value || []).includes(o.value))
    : opts.find((o) => o.value === value) || null;

  const handleChange = (selected) => {
    if (isMulti) onChange(selected ? selected.map((s) => s.value) : []);
    else         onChange(selected ? selected.value : "");
  };

  return (
    <Select
      options={opts}
      value={resolvedValue}
      onChange={handleChange}
      placeholder={placeholder}
      isMulti={isMulti}
      isDisabled={isDisabled}
      isClearable={isClearable}
      styles={size === "sm" ? selectStylesCompact : selectStyles}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      {...rest}
    />
  );
};

// ─── DateRangePill — matches FeedbackResponses DatePicker exactly ─────────────
// Props: startDate (moment), endDate (moment), onChange({start,end})
export const DateRangePill = ({ startDate, endDate, onChange }) => {
  const start = startDate || moment().startOf("month");
  const end   = endDate   || moment().endOf("month");

  return (
    <DatePicker
      initialSettings={{
        singleDatePicker: false,
        showDropdowns:    true,
        startDate:        start.toDate(),
        endDate:          end.toDate(),
        ranges: {
          "Today":       [moment().toDate(),                              moment().toDate()],
          "This Week":   [moment().startOf("week").toDate(),              moment().endOf("week").toDate()],
          "This Month":  [moment().startOf("month").toDate(),             moment().endOf("month").toDate()],
          "Last 7 Days": [moment().subtract(7, "days").toDate(),          moment().toDate()],
          "Last 30 Days":[moment().subtract(30, "days").toDate(),         moment().toDate()],
          "Last 90 Days":[moment().subtract(90, "days").toDate(),         moment().toDate()],
          "Next Month":  [moment().add(1,"month").startOf("month").toDate(), moment().add(1,"month").endOf("month").toDate()],
        },
        locale: { format: "DD MMM YYYY", applyLabel: "Select", cancelLabel: "Cancel" },
      }}
      onCallback={(s, e) => onChange({ start: moment(s).startOf("day"), end: moment(e).endOf("day") })}
    >
      <div className="input-group" style={{ maxWidth: 200 }}>
        <input
          type="text"
          className="form-control"
          readOnly
          value={`${start.format("DD MMM")} – ${end.format("DD MMM YYYY")}`}
          style={{
            height:          40,
            fontSize:        14,
            fontWeight:      600,
            textAlign:       "center",
            padding:         "0 10px",
            borderRight:     "none",
            backgroundColor: "#fff",
            borderRadius:    "999px 0 0 999px",
            cursor:          "pointer",
          }}
        />
        <span
          className="input-group-text"
          style={{
            height:          40,
            backgroundColor: "#fff",
            borderLeft:      "none",
            borderRadius:    "0 999px 999px 0",
            cursor:          "pointer",
          }}
        >
          <i className="fa fa-chevron-down" style={{ color: "#495057", fontSize: 12 }} />
        </span>
      </div>
    </DatePicker>
  );
};

// ─── RDatePicker — single date, wraps react-datepicker ───────────────────────
export const RDatePicker = ({ value, onChange, placeholder = "Select date", disabled = false, minDate, maxDate }) => (
  <ReactDatePicker
    selected={value ? new Date(value) : null}
    onChange={(date) => onChange(date ? moment(date).format("YYYY-MM-DD") : "")}
    dateFormat="dd MMM yyyy"
    placeholderText={placeholder}
    disabled={disabled}
    minDate={minDate}
    maxDate={maxDate}
    className="form-control"
    wrapperClassName="w-100"
    showMonthDropdown
    showYearDropdown
    dropdownMode="select"
    popperPlacement="bottom-start"
    autoComplete="off"
  />
);

// ─── EngagementDatePicker ─────────────────────────────────────────────────────
// Custom date-range picker matching the dashboard screenshot:
//   Left panel: preset shortcuts
//   Right panel: dual-input (From/To) + month calendar + Cancel/Reset/Apply
//
// Props:
//   startDate  — moment or null
//   endDate    — moment or null
//   onChange({ start, end }) — called on Apply with moment values
//   placeholder — string shown on trigger pill
//   minDate    — moment (optional lower bound)
//   maxDate    — moment (optional upper bound)
//   align      — "left" | "right" (default "left")
// ─────────────────────────────────────────────────────────────────────────────
const C_DP = {
  brand:"#1a56db", brandLt:"#ebf5ff", brandDk:"#1e429f",
  text:"#111827", muted:"#6b7280", subtle:"#9ca3af",
  border:"#e5e7eb", bg:"#f9fafb", white:"#ffffff",
  today:"#fef3c7", todayBorder:"#f59e0b",
};

const PRESETS = [
  { label:"Today",          fn:() => ({ s: moment().startOf("day"),           e: moment().endOf("day")           }) },
  { label:"Yesterday",      fn:() => ({ s: moment().subtract(1,"day").startOf("day"), e: moment().subtract(1,"day").endOf("day") }) },
  { label:"This Week",      fn:() => ({ s: moment().startOf("week"),          e: moment().endOf("week")          }) },
  { label:"Last Week",      fn:() => ({ s: moment().subtract(1,"week").startOf("week"), e: moment().subtract(1,"week").endOf("week") }) },
  { label:"This Month",     fn:() => ({ s: moment().startOf("month"),         e: moment().endOf("month")         }) },
  { label:"Last Month",     fn:() => ({ s: moment().subtract(1,"month").startOf("month"), e: moment().subtract(1,"month").endOf("month") }) },
  { label:"This Quarter to Date", fn:() => ({ s: moment().startOf("quarter"),e: moment()                        }) },
  { label:"Last Quarter",   fn:() => ({ s: moment().subtract(1,"quarter").startOf("quarter"), e: moment().subtract(1,"quarter").endOf("quarter") }) },
  { label:"Last 7 Days",    fn:() => ({ s: moment().subtract(6,"days").startOf("day"), e: moment().endOf("day") }) },
  { label:"Last 30 Days",   fn:() => ({ s: moment().subtract(29,"days").startOf("day"),e: moment().endOf("day") }) },
  { label:"Last 90 Days",   fn:() => ({ s: moment().subtract(89,"days").startOf("day"),e: moment().endOf("day") }) },
];

const fmtInput  = d => d ? moment(d).format("DD/MM/YYYY") : "";
const fmtPill   = (s,e) => s && e ? `${moment(s).format("D MMM YY")} – ${moment(e).format("D MMM YY")}` : "Select dates";
const parseInput = str => { const m = moment(str,"DD/MM/YYYY",true); return m.isValid() ? m : null; };
const DAYS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

function MiniCal({ viewDate, setViewDate, rangeStart, rangeEnd, hoverDate, setHoverDate, selecting, onDayClick }) {
  const year  = viewDate.year();
  const month = viewDate.month();
  const first = moment({ year, month, day: 1 });
  const startPad = first.day(); // 0=Sun
  const daysInMonth = viewDate.daysInMonth();

  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(moment({ year, month, day: d }));

  const isStart  = d => d && rangeStart && d.isSame(rangeStart,"day");
  const isEnd    = d => d && rangeEnd   && d.isSame(rangeEnd,  "day");
  const isToday  = d => d && d.isSame(moment(),"day");
  const hoverEnd = rangeStart && !rangeEnd && hoverDate;
  const inRange  = d => {
    if (!d) return false;
    const lo = rangeStart, hi = rangeEnd || hoverEnd;
    if (!lo || !hi) return false;
    return d.isAfter(lo,"day") && d.isBefore(hi,"day") || d.isAfter(hi,"day") && d.isBefore(lo,"day");
  };

  return (
    <div style={{ userSelect:"none" }}>
      {/* Month nav */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
        marginBottom:10 }}>
        <button onClick={() => setViewDate(viewDate.clone().subtract(1,"month"))}
          style={{ border:"none", background:"none", cursor:"pointer",
            color:C_DP.brand, fontWeight:800, fontSize:"1rem", padding:"0 4px" }}>
          ‹
        </button>
        <span style={{ fontSize:"0.88rem", fontWeight:700, color:C_DP.text }}>
          {viewDate.format("MMM YYYY")}
        </span>
        <button onClick={() => setViewDate(viewDate.clone().add(1,"month"))}
          style={{ border:"none", background:"none", cursor:"pointer",
            color:C_DP.brand, fontWeight:800, fontSize:"1rem", padding:"0 4px" }}>
          ›
        </button>
      </div>

      {/* Day headers */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)",
        marginBottom:4 }}>
        {DAYS.map(d => (
          <div key={d} style={{ textAlign:"center", fontSize:"0.68rem",
            fontWeight:700, color:C_DP.subtle, padding:"2px 0" }}>
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:1 }}>
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const start  = isStart(d);
          const end    = isEnd(d);
          const range  = inRange(d);
          const today  = isToday(d);
          const endOrHover = end || (rangeStart && !rangeEnd && hoverEnd && d.isSame(hoverEnd,"day"));

          let bg = "transparent", color = C_DP.text, borderRadius = "6px";
          if (start || endOrHover) { bg = C_DP.brand; color = C_DP.white; }
          else if (range) { bg = C_DP.brandLt; borderRadius = "0"; }
          if (today && !start && !endOrHover && !range) { bg = C_DP.today; }

          return (
            <div key={d.date()} onClick={() => onDayClick(d)}
              onMouseEnter={() => setHoverDate(d)}
              onMouseLeave={() => setHoverDate(null)}
              style={{ textAlign:"center", padding:"5px 0",
                fontSize:"0.8rem", fontWeight: start || endOrHover ? 700 : 400,
                background: bg, color, borderRadius,
                cursor:"pointer", transition:"background 0.1s",
                border: today && !start && !endOrHover ? `1px solid ${C_DP.todayBorder}` : "1px solid transparent",
              }}>
              {d.date()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const EngagementDatePicker = ({
  startDate, endDate, onChange,
  placeholder, minDate, maxDate,
  align = "left",
}) => {
  const [open,       setOpen]       = useState(false);
  const [viewDate,   setViewDate]   = useState(() => (startDate ? moment(startDate) : moment()).startOf("month"));
  const [tempStart,  setTempStart]  = useState(() => startDate ? moment(startDate) : null);
  const [tempEnd,    setTempEnd]    = useState(() => endDate   ? moment(endDate)   : null);
  const [fromInput,  setFromInput]  = useState(() => fmtInput(startDate));
  const [toInput,    setToInput]    = useState(() => fmtInput(endDate));
  const [hoverDate,  setHoverDate]  = useState(null);
  const [selecting,  setSelecting]  = useState("start"); // "start" | "end"
  const [activePreset, setActivePreset] = useState(null);
  const ref = useRef();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Sync from/to inputs when temp dates change
  useEffect(() => { setFromInput(fmtInput(tempStart)); }, [tempStart]);
  useEffect(() => { setToInput(fmtInput(tempEnd));     }, [tempEnd]);

  const applyPreset = (preset) => {
    const { s, e } = preset.fn();
    setTempStart(s); setTempEnd(e);
    setViewDate(s.clone().startOf("month"));
    setActivePreset(preset.label);
    setSelecting("start");
  };

  const onDayClick = (d) => {
    setActivePreset(null);
    if (selecting === "start" || (tempStart && tempEnd)) {
      setTempStart(d); setTempEnd(null); setSelecting("end");
    } else {
      if (d.isBefore(tempStart,"day")) {
        setTempEnd(tempStart); setTempStart(d);
      } else {
        setTempEnd(d);
      }
      setSelecting("start");
    }
  };

  const handleFromInput = (v) => {
    setFromInput(v);
    const m = parseInput(v);
    if (m) { setTempStart(m); setViewDate(m.clone().startOf("month")); }
  };
  const handleToInput = (v) => {
    setToInput(v);
    const m = parseInput(v);
    if (m) setTempEnd(m);
  };

  const apply = () => {
    if (tempStart && tempEnd) {
      const s = tempStart.isBefore(tempEnd) ? tempStart : tempEnd;
      const e = tempStart.isBefore(tempEnd) ? tempEnd   : tempStart;
      onChange({ start: s.startOf("day"), end: e.endOf("day") });
      setOpen(false);
    }
  };

  const reset = () => { setTempStart(null); setTempEnd(null); setActivePreset(null); setSelecting("start"); };
  const cancel = () => { setTempStart(startDate?moment(startDate):null); setTempEnd(endDate?moment(endDate):null); setOpen(false); };

  const pillLabel = fmtPill(startDate, endDate);

  return (
    <div ref={ref} style={{ position:"relative", display:"inline-block" }}>
      {/* Trigger pill */}
      <button onClick={() => setOpen(o => !o)}
        style={{ display:"inline-flex", alignItems:"center", gap:8,
          padding:"7px 14px", borderRadius:99,
          border:`1.5px solid ${open ? C_DP.brand : C_DP.border}`,
          background: open ? C_DP.brandLt : C_DP.white,
          color: startDate ? C_DP.text : C_DP.subtle,
          fontSize:"0.82rem", fontWeight:600, cursor:"pointer",
          boxShadow: open ? `0 0 0 3px ${C_DP.brandLt}` : "none",
          whiteSpace:"nowrap" }}>
        <i className="fas fa-calendar-alt" style={{ color:C_DP.brand, fontSize:"0.76rem" }} />
        {pillLabel}
        <i className="fas fa-chevron-down" style={{ fontSize:"0.65rem", color:C_DP.muted,
          transform: open ? "rotate(180deg)" : "none", transition:"transform 0.15s" }} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 8px)",
          [align === "right" ? "right" : "left"]: 0,
          zIndex:1200,
          background:C_DP.white, borderRadius:14,
          boxShadow:"0 8px 40px rgba(0,0,0,0.18)",
          border:`1px solid ${C_DP.border}`,
          display:"flex", overflow:"hidden",
          minWidth:580,
        }}>
          {/* Left: presets */}
          <div style={{ width:180, flexShrink:0, borderRight:`1px solid ${C_DP.border}`,
            padding:"12px 0", background:C_DP.bg }}>
            {PRESETS.map(p => (
              <button key={p.label} onClick={() => applyPreset(p)}
                style={{ display:"block", width:"100%", textAlign:"left",
                  padding:"8px 18px", border:"none", background:"none",
                  fontSize:"0.81rem", fontWeight: activePreset===p.label ? 700 : 400,
                  color: activePreset===p.label ? C_DP.brand : C_DP.text,
                  background: activePreset===p.label ? C_DP.brandLt : "none",
                  cursor:"pointer",
                  borderLeft: activePreset===p.label ? `3px solid ${C_DP.brand}` : "3px solid transparent",
                }}>
                {p.label}
              </button>
            ))}
          </div>

          {/* Right: inputs + calendar + footer */}
          <div style={{ flex:1, padding:"16px 18px", display:"flex",
            flexDirection:"column", gap:14 }}>

            {/* From / To inputs */}
            <div style={{ display:"flex", gap:10, alignItems:"center" }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:"0.65rem", fontWeight:800, textTransform:"uppercase",
                  letterSpacing:"0.07em", color:C_DP.subtle, marginBottom:4 }}>
                  From *
                </div>
                <input value={fromInput} onChange={e => handleFromInput(e.target.value)}
                  placeholder="DD/MM/YYYY"
                  style={{ width:"100%", padding:"7px 10px", fontSize:"0.82rem",
                    border:`1.5px solid ${selecting==="start" ? C_DP.brand : C_DP.border}`,
                    borderRadius:8, outline:"none", color:C_DP.text, boxSizing:"border-box" }}
                  onFocus={() => setSelecting("start")} />
              </div>
              <div style={{ color:C_DP.subtle, marginTop:18 }}>→</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:"0.65rem", fontWeight:800, textTransform:"uppercase",
                  letterSpacing:"0.07em", color:C_DP.subtle, marginBottom:4 }}>
                  To *
                </div>
                <input value={toInput} onChange={e => handleToInput(e.target.value)}
                  placeholder="DD/MM/YYYY"
                  style={{ width:"100%", padding:"7px 10px", fontSize:"0.82rem",
                    border:`1.5px solid ${selecting==="end" ? C_DP.brand : C_DP.border}`,
                    borderRadius:8, outline:"none", color:C_DP.text, boxSizing:"border-box" }}
                  onFocus={() => setSelecting("end")} />
              </div>
            </div>

            {/* Calendar */}
            <MiniCal
              viewDate={viewDate} setViewDate={setViewDate}
              rangeStart={tempStart} rangeEnd={tempEnd}
              hoverDate={hoverDate} setHoverDate={setHoverDate}
              selecting={selecting} onDayClick={onDayClick}
            />

            {/* Footer */}
            <div style={{ display:"flex", justifyContent:"flex-end",
              gap:8, paddingTop:8, borderTop:`1px solid ${C_DP.border}` }}>
              <button onClick={cancel}
                style={{ padding:"7px 16px", borderRadius:8,
                  border:`1px solid ${C_DP.border}`, background:C_DP.white,
                  color:C_DP.text, fontSize:"0.82rem", fontWeight:600, cursor:"pointer" }}>
                Cancel
              </button>
              <button onClick={reset}
                style={{ padding:"7px 16px", borderRadius:8,
                  border:`1px solid ${C_DP.border}`, background:C_DP.white,
                  color:C_DP.muted, fontSize:"0.82rem", fontWeight:600, cursor:"pointer" }}>
                Reset
              </button>
              <button onClick={apply}
                disabled={!tempStart || !tempEnd}
                style={{ padding:"7px 20px", borderRadius:8, border:"none",
                  background: tempStart && tempEnd ? C_DP.brand : C_DP.border,
                  color:C_DP.white, fontSize:"0.82rem", fontWeight:700,
                  cursor: tempStart && tempEnd ? "pointer" : "not-allowed" }}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── SingleDatePicker — clean styled single-date picker ──────────────────────
// Replaces native <input type="date"> in campaign form and journey builder
// Props: value (YYYY-MM-DD string), onChange(YYYY-MM-DD), placeholder
export const SingleDatePicker = ({ value, onChange, placeholder = "Select date", disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => value ? moment(value).startOf("month") : moment().startOf("month"));
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  useEffect(() => { if (value) setViewDate(moment(value).startOf("month")); }, [value]);

  const year  = viewDate.year();
  const month = viewDate.month();
  const first = moment({ year, month, day: 1 });
  const pad   = first.day();
  const days  = viewDate.daysInMonth();
  const cells = [];
  for (let i = 0; i < pad; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(moment({ year, month, day: d }));

  const selected  = value ? moment(value) : null;
  const isToday   = d => d && d.isSame(moment(), "day");
  const isSel     = d => d && selected && d.isSame(selected, "day");

  const pick = (d) => {
    if (!d || disabled) return;
    onChange(d.format("YYYY-MM-DD"));
    setOpen(false);
  };

  const displayVal = value
    ? moment(value).format("DD MMM YYYY")
    : placeholder;

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button onClick={() => !disabled && setOpen(o => !o)}
        style={{ display:"inline-flex", alignItems:"center", gap:8, width:"100%",
          padding:"8px 12px", borderRadius:9,
          border:`1.5px solid ${open ? C_DP.brand : C_DP.border}`,
          background: disabled ? C_DP.bg : C_DP.white,
          color: value ? C_DP.text : C_DP.subtle,
          fontSize:"0.84rem", fontWeight: value ? 600 : 400,
          cursor: disabled ? "not-allowed" : "pointer",
          boxShadow: open ? `0 0 0 3px ${C_DP.brandLt}` : "none",
          textAlign:"left", boxSizing:"border-box" }}>
        <i className="fas fa-calendar-alt" style={{ color: disabled ? C_DP.border : C_DP.brand, fontSize:"0.8rem", flexShrink:0 }} />
        <span style={{ flex:1 }}>{displayVal}</span>
        {value && !disabled && (
          <span onClick={e => { e.stopPropagation(); onChange(""); }}
            style={{ color:C_DP.subtle, fontSize:"0.9rem", lineHeight:1, flexShrink:0 }}>
            ×
          </span>
        )}
      </button>

      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, zIndex:1200,
          background:C_DP.white, borderRadius:12,
          boxShadow:"0 6px 32px rgba(0,0,0,0.15)",
          border:`1px solid ${C_DP.border}`, padding:14, minWidth:260 }}>

          {/* Month nav */}
          <div style={{ display:"flex", alignItems:"center",
            justifyContent:"space-between", marginBottom:10 }}>
            <button onClick={() => setViewDate(viewDate.clone().subtract(1,"month"))}
              style={{ border:"none", background:"none", cursor:"pointer",
                color:C_DP.brand, fontWeight:800, fontSize:"1.1rem", padding:"0 4px" }}>‹</button>
            <span style={{ fontSize:"0.88rem", fontWeight:700, color:C_DP.text }}>
              {viewDate.format("MMMM YYYY")}
            </span>
            <button onClick={() => setViewDate(viewDate.clone().add(1,"month"))}
              style={{ border:"none", background:"none", cursor:"pointer",
                color:C_DP.brand, fontWeight:800, fontSize:"1.1rem", padding:"0 4px" }}>›</button>
          </div>

          {/* Day headers */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", marginBottom:4 }}>
            {DAYS.map(d => (
              <div key={d} style={{ textAlign:"center", fontSize:"0.67rem",
                fontWeight:700, color:C_DP.subtle }}>{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
            {cells.map((d, i) => {
              if (!d) return <div key={`e${i}`} />;
              const sel   = isSel(d);
              const today = isToday(d);
              return (
                <div key={d.date()} onClick={() => pick(d)}
                  style={{ textAlign:"center", padding:"6px 0",
                    fontSize:"0.8rem", fontWeight: sel ? 700 : 400,
                    background: sel ? C_DP.brand : today ? C_DP.today : "transparent",
                    color: sel ? C_DP.white : C_DP.text,
                    borderRadius:7, cursor:"pointer",
                    border: today && !sel ? `1px solid ${C_DP.todayBorder}` : "1px solid transparent",
                    transition:"background 0.1s",
                  }}
                  onMouseEnter={e => { if (!sel) e.target.style.background = C_DP.brandLt; }}
                  onMouseLeave={e => { if (!sel) e.target.style.background = today ? C_DP.today : "transparent"; }}>
                  {d.date()}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── RightModal — full-height right-panel modal (modal-right pattern) ─────────
// Matches FeedbackSetting.jsx modal-right exactly
// Props: show, onHide, title, children, footer, size ("xl" default), scrollable
export const RightModal = ({
  show,
  onHide,
  title,
  children,
  footer,
  size    = "xl",
  scrollable = true,
}) => (
  <>
    <Modal
      show={show}
      onHide={onHide}
      size={size}
      dialogClassName="modal-right"
      style={{ height: "100vh", margin: 0 }}
      aria-label={title}
    >
      {/* Prominent close button */}
      <Modal.Header
        style={{
          background:    "#fff",
          borderBottom:  "1px solid #e5e7eb",
          padding:       "16px 24px",
          position:      "sticky",
          top:           0,
          zIndex:        10,
        }}
      >
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 700 }}>{title}</Modal.Title>
        {/* Large, visible close button */}
        <button
          onClick={onHide}
          aria-label="Close"
          style={{
            background:   "#f3f4f6",
            border:       "none",
            borderRadius: "50%",
            width:        36,
            height:       36,
            display:      "flex",
            alignItems:   "center",
            justifyContent: "center",
            cursor:       "pointer",
            fontSize:     18,
            color:        "#374151",
            flexShrink:   0,
            marginLeft:   "auto",
            transition:   "background 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.color = "#dc2626"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.color = "#374151"; }}
        >
          ×
        </button>
      </Modal.Header>

      <Modal.Body
        style={{
          overflowY:  scrollable ? "auto" : "visible",
          padding:    "24px",
          flexGrow:   1,
          height:     footer ? "calc(100vh - 130px)" : "calc(100vh - 66px)",
        }}
      >
        {children}
      </Modal.Body>

      {footer && (
        <Modal.Footer
          style={{
            position:      "sticky",
            bottom:        0,
            background:    "#fff",
            borderTop:     "1px solid #e5e7eb",
            padding:       "12px 24px",
            zIndex:        10,
            display:       "flex",
            justifyContent:"flex-end",
            gap:           8,
          }}
        >
          {footer}
        </Modal.Footer>
      )}
    </Modal>

    {/* modal-right CSS — injected once */}
    <style>{`
      .modal-right .modal-dialog {
        position: fixed !important;
        top: 0 !important;
        right: 0 !important;
        margin: 0 !important;
        height: 100vh !important;
        max-height: 100vh !important;
        transform: none !important;
        display: flex;
        flex-direction: column;
      }
      .modal-right .modal-content {
        height: 100vh !important;
        border-radius: 0 !important;
        display: flex;
        flex-direction: column;
        border: none;
        box-shadow: -4px 0 24px rgba(0,0,0,0.12);
      }
      .modal-right .modal-body {
        overflow-y: auto;
        flex: 1;
      }
      .modal-right .modal-header {
        flex-shrink: 0;
      }
      .modal-right .modal-footer {
        flex-shrink: 0;
      }
    `}</style>
  </>
);

// ─── DownloadMenu — matches FeedbackResponses downloadMenu exactly ────────────
// exportData: array of row objects
// headers: array of { label, key } objects
// filename: base filename without extension
// onPrint: optional callback for print preview
export const DownloadMenu = ({ exportData = [], headers = [], filename = "export", onPrint, disabled = false }) => {
  const [busy, setBusy] = useState(false);

  const suffix = `_${moment().format("YYYYMMDD")}`;

  const doExport = async (type) => {
    setBusy(true);
    try {
      // Dynamic import of exportService (matches feedback pattern)
      const { default: exportService } = await import("../../../pages/pos/utils/exportService")
        .catch(() => ({ default: buildFallbackExportService() }));

      if (type === "csv")   exportService.exportToCSV(exportData, headers, `${filename}${suffix}.csv`);
      if (type === "xls")   exportService.exportToXLS(exportData, headers, `${filename}${suffix}.xlsx`, filename);
      if (type === "print") onPrint ? onPrint() : exportService.exportToCSV(exportData, headers, `${filename}${suffix}.csv`);
    } catch {
      fallbackCSV(exportData, headers, `${filename}${suffix}.csv`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        variant="outline-secondary"
        size="sm"
        disabled={disabled || busy}
        className="d-inline-flex align-items-center gap-2"
        style={{ borderRadius: 6, fontWeight: 500 }}
      >
        {busy
          ? <><Spinner size="sm" animation="border" className="me-1" />Exporting...</>
          : <><i className="la la-download me-1" />Download <FaChevronDown size={10} className="ms-1" /></>}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => doExport("csv")}>
          <i className="la la-file-csv me-2 text-success" />Download CSV
        </Dropdown.Item>
        <Dropdown.Item onClick={() => doExport("xls")}>
          <i className="la la-file-excel me-2 text-success" />Download XLS
        </Dropdown.Item>
        {onPrint && (
          <>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => doExport("print")}>
              <i className="la la-print me-2 text-secondary" />Print Preview
            </Dropdown.Item>
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

// Fallback CSV builder when exportService import fails
const fallbackCSV = (data, headers, filename) => {
  const header = headers.map((h) => h.label || h.key).join(",");
  const rows   = data.map((row) => headers.map((h) => `"${row[h.key] ?? ""}"`).join(","));
  const blob   = new Blob([`${header}\n${rows.join("\n")}`], { type: "text/csv" });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const buildFallbackExportService = () => ({
  exportToCSV:  (data, headers, filename) => fallbackCSV(data, headers, filename),
  exportToXLS:  (data, headers, filename) => fallbackCSV(data, headers, filename.replace(".xlsx",".csv")),
  exportToPDF:  () => {},
});

// ─── AiAssistBtn — inline contextual AI button ────────────────────────────────
// Only renders when AI_ENABLED = true
// fieldHint: brief description of what AI should help with
// onResult: callback with the AI-generated string
export const AiAssistBtn = ({ fieldHint = "this field", systemHint = "", onResult, context = {} }) => {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  if (!AI_ENABLED) return null;

  const assist = async () => {
    setLoading(true);
    setError("");
    try {
      const system = systemHint || `You are a hospitality marketing assistant. Give a single, short, direct answer.`;
      const prompt = `Help me write content for: ${fieldHint}.
${Object.entries(context).map(([k,v]) => `${k}: ${v}`).join("\n")}
Keep it concise, professional, hospitality-appropriate.`;

      const res = await axios.post(
        `${apiUrl}/ai/generate`,
        { system, prompt, max_tokens: 200 },
        authHeaders()
      );
      const text = res.data?.text || res.data?.content || "";
      if (text && onResult) onResult(text.trim());
    } catch {
      setError("AI unavailable");
      setTimeout(() => setError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={assist}
      disabled={loading}
      title={`AI: generate ${fieldHint}`}
      style={{
        background:    loading ? "#e0e7ff" : "linear-gradient(135deg,#6366f1,#8b5cf6)",
        border:        "none",
        borderRadius:  6,
        color:         "#fff",
        padding:       "3px 8px",
        fontSize:      "0.72rem",
        fontWeight:    600,
        cursor:        "pointer",
        display:       "inline-flex",
        alignItems:    "center",
        gap:           4,
        whiteSpace:    "nowrap",
        transition:    "all 0.15s",
      }}
    >
      {loading
        ? <><Spinner size="sm" animation="border" style={{ width:10, height:10 }} /> AI…</>
        : <><FaMagic size={9} /> AI</>}
    </button>
  );
};

// ─── useNotify shim ───────────────────────────────────────────────────────────
// Usage in engagement components:
//   import { useNotify } from "./shared";
//   const notify = useNotify();
//   notify("success", "Saved!");
//
// If useNotification context is available it uses that; otherwise falls back
// to a Bootstrap toast DOM injection so it works standalone.
export const useNotify = () => {
  try {
    // eslint-disable-next-line
    const { useNotification } = require("../../../context/NotificationContext");
    // eslint-disable-next-line
    const { notify } = useNotification();
    return notify;
  } catch {
    // Fallback: inject a small toast
    return (type, message) => {
      const id    = `toast_${Date.now()}`;
      const bg    = type === "success" ? "#16a34a" : type === "error" ? "#dc2626" : type === "warning" ? "#d97706" : "#2563eb";
      const el    = document.createElement("div");
      el.id       = id;
      el.setAttribute("role", "alert");
      el.style.cssText = `
        position:fixed;top:20px;right:20px;z-index:99999;
        background:${bg};color:#fff;padding:10px 18px;
        border-radius:8px;font-size:0.88rem;font-weight:600;
        box-shadow:0 4px 16px rgba(0,0,0,0.18);
        max-width:320px;word-break:break-word;
        animation:fadeInToast 0.25s ease;
      `;
      el.textContent = message;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 3500);
      if (!document.getElementById("toast-style")) {
        const s = document.createElement("style");
        s.id = "toast-style";
        s.textContent = "@keyframes fadeInToast{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}";
        document.head.appendChild(s);
      }
    };
  }
};
